import React, { CSSProperties, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';

import BasicModeButton from '../../../elements/BasicModeButton';

import { useDetailViewContext } from '../../../context/DetailViewContext';
import { useAudioContext } from '../../../context/AudioContext';
import { useNavigationContext } from '../../../context/NavigationContext';
import { DetailSectionIDs, SectionIDs } from '../../../data/Sections';
import FocusPoints from '../../../data/FocusPoints';

import { ReactComponent as ArrowLeftIcon } from '../../../assets/images/icons/arrow-left.svg';
import styles from './styles.module.scss';
import whiteModeStyles from '../WhiteModeSidePanel/styles.module.scss';

import useIsMobile from '../../../hooks/useIsMobile';

interface IDarkMode {
  id: DetailSectionIDs,
  details: IDetail[]
}

interface IDetail {
  id: FocusPoints, 
  title?: string, 
  title2?: string, 
  text: string,
}

export default function DarkModeSidePanel({ style }: {style:CSSProperties}): JSX.Element {
  const isMobile = useIsMobile();
  const { playAudio } = useAudioContext();
  const {moveToTheSection} = useNavigationContext();
  const {activeDetailID, activeFocusID, setActiveFocusID, setDetailSectionID} = useDetailViewContext();
  const {t} = useTranslation('ai4Jokes');

  const data = useMemo<IDarkMode[]>(() => [
    {
      id: DetailSectionIDs.DecodingSubspaces,
      details: [
        {
          id: FocusPoints.Overview,
          title: t('spheresubspaces.advanced.overview.title_1'),
          title2: t('spheresubspaces.advanced.overview.title_2'),
          text: t('spheresubspaces.advanced.overview.paragraph'),
        },{
          id: FocusPoints.SubspaceHusband,
          title2: t('spheresubspaces.advanced.husband.title'),
          text: t('spheresubspaces.advanced.husband.paragraph'),
        },
        {
          id:FocusPoints.SubspaceCat,
          title2: t('spheresubspaces.advanced.cat.title'),
          text: t('spheresubspaces.advanced.cat.paragraph'),
        }]
    },
    {
      id: DetailSectionIDs.DecodingTopology,
      details: [{
        id: FocusPoints.Overview,
        title: t('topologicalrelations.advanced.overview.title_1'),
        title2: t('topologicalrelations.advanced.overview.title_2'),
        text: t('topologicalrelations.advanced.overview.paragraph'),
      },{
        id: FocusPoints.TopologicalHusband,
        title2: t('topologicalrelations.advanced.husband.title'),
        text: t('topologicalrelations.advanced.husband.paragraph'),
      },
      {
        id:FocusPoints.TopoligicalWife,
        title2: t('topologicalrelations.advanced.wife.title'),
        text: t('topologicalrelations.advanced.wife.paragraph'),
      },
      {
        id:FocusPoints.TopoligicalCat,
        title2: t('topologicalrelations.advanced.cat.title'),
        text: t('topologicalrelations.advanced.cat.paragraph'),
      }]
    },
    {
      id: DetailSectionIDs.DecodingDynamicBehaviour,
      details: [{
        id: FocusPoints.Overview,
        title: t('dynamicbehavior.advanced.overview.title_1'),
        title2: t('dynamicbehavior.advanced.overview.title_2'),
        text: t('dynamicbehavior.advanced.overview.paragraph'),
      },{
        id: FocusPoints.DynamicBehaviorExpectation,
        title2: t('dynamicbehavior.advanced.expectation.title'),
        text: t('dynamicbehavior.advanced.expectation.paragraph'),
      },
      {
        id:FocusPoints.DynamicBehaviorReality,
        title2: t('dynamicbehavior.advanced.reality.title'),
        text: t('dynamicbehavior.advanced.reality.paragraph'),
      }]
    }
  ], [t]);

  const selectedFocus = useMemo(() => 
    data.find(d => d.id === activeDetailID)?.details.
      find(d => d.id === activeFocusID),
  [activeDetailID, activeFocusID, data]);

  const onBasicModeClick = useCallback(
    () => {
      playAudio('audioBasicMode');
      setActiveFocusID(FocusPoints.None);
    },
    [setActiveFocusID, playAudio],
  );

  const onToTimelineClick = useCallback(
    () => {
      playAudio('audioCTA');
      setActiveFocusID(FocusPoints.None);
      setDetailSectionID(DetailSectionIDs.None);
    },
    [setActiveFocusID, setDetailSectionID, playAudio],
  );

  const toNextDetail = useCallback(
    () => {
      playAudio('audioNextPrev');
      switch (activeDetailID) {
      case DetailSectionIDs.DecodingSubspaces:
        setDetailSectionID(DetailSectionIDs.DecodingTopology);
        break;
      case DetailSectionIDs.DecodingTopology:
        setDetailSectionID(DetailSectionIDs.DecodingDynamicBehaviour);
        break;
      default:
        break;
      }
      setActiveFocusID(FocusPoints.None);
    },
    [activeDetailID, setDetailSectionID, setActiveFocusID, playAudio],
  );

  const toConclusion = useCallback(
    () => {
      playAudio('audioCTA');
      setDetailSectionID(DetailSectionIDs.None);
      moveToTheSection(SectionIDs.Conclusion);
    },
    [setDetailSectionID, moveToTheSection, playAudio],
  );

  const onFocusIDClick = useCallback((event, focusPoint) => {
    setActiveFocusID(focusPoint);
    event.nativeEvent.stopImmediatePropagation();
  }, [setActiveFocusID]);

  return (
    <div className={[whiteModeStyles.whiteModeSidePanel, styles.darkModeSidePanel].join(' ')} style={style}>
      <BasicModeButton click={onBasicModeClick}></BasicModeButton>
      <div className={[whiteModeStyles.innerContainer, styles.innerContainer].join(' ')}>

        {selectedFocus?.title && <div className={whiteModeStyles.title}>{selectedFocus?.title}</div>}

        {selectedFocus?.title2 && <div className={whiteModeStyles.title2}>{selectedFocus?.title2}</div>}

        <div className={styles.content}>
          <div className={[whiteModeStyles.text, whiteModeStyles.contentCustomScroll, styles.contentCustomScroll].join(' ')}>
            {parse(selectedFocus?.text || '')}
          </div>
        </div>

        <div className={styles.buttonsWrapper}>
          {activeDetailID === DetailSectionIDs.DecodingSubspaces && 
            <div className={styles.sphereButtonContainer}>
              <button
                onClick={(event) => onFocusIDClick(event, FocusPoints.Overview)}
                data-selected={activeFocusID === FocusPoints.Overview}
                className={styles.overviewButton}>
                {t('spheresubspaces.advanced.overview.button')}
              </button>
              <button
                onClick={(event) => onFocusIDClick(event, FocusPoints.SubspaceCat)}
                data-selected={activeFocusID === FocusPoints.SubspaceCat}
                className={[styles.overviewButton, styles.cat].join(' ')}>
                {t('spheresubspaces.advanced.cat.button')}
              </button>
              <button 
                onClick={(event) => onFocusIDClick(event, FocusPoints.SubspaceHusband)} 
                data-selected={activeFocusID === FocusPoints.SubspaceHusband}
                className={[styles.overviewButton, styles.man].join(' ')}>
                {t('spheresubspaces.advanced.husband.button')}
              </button>
            </div>
          }

          {activeDetailID === DetailSectionIDs.DecodingTopology && 
            <div className={styles.sphereButtonContainer}>
              <button 
                onClick={(event) => onFocusIDClick(event, FocusPoints.Overview)}
                data-selected={activeFocusID === FocusPoints.Overview}
                className={styles.overviewButton}>
                {t('topologicalrelations.advanced.overview.button')}
              </button>
              <button 
                onClick={(event) => onFocusIDClick(event, FocusPoints.TopoligicalWife)}
                data-selected={activeFocusID === FocusPoints.TopoligicalWife}
                className={[styles.overviewButton, styles.wife].join(' ')}>
                {t('topologicalrelations.advanced.wife.button')}
              </button>
              <button 
                onClick={(event) => onFocusIDClick(event, FocusPoints.TopoligicalCat)}
                data-selected={activeFocusID === FocusPoints.TopoligicalCat}
                className={[styles.overviewButton, styles.cat].join(' ')}>
                {t('topologicalrelations.advanced.cat.button')}
              </button>
              <button 
                onClick={(event) => onFocusIDClick(event, FocusPoints.TopologicalHusband)}
                data-selected={activeFocusID === FocusPoints.TopologicalHusband}
                className={[styles.overviewButton, styles.man].join(' ')}>
                {t('topologicalrelations.advanced.husband.button')}
              </button>
            </div>
          }

          { activeDetailID === DetailSectionIDs.DecodingDynamicBehaviour && 
            <div className={styles.sphereButtonContainer}>
              <button 
                onClick={(event) => onFocusIDClick(event, FocusPoints.Overview)}
                data-selected={activeFocusID === FocusPoints.Overview}
                className={styles.overviewButton}>
                {t('dynamicbehavior.advanced.overview.button')}
              </button>
              <button 
                onClick={(event) => onFocusIDClick(event, FocusPoints.DynamicBehaviorExpectation)}
                data-selected={activeFocusID === FocusPoints.DynamicBehaviorExpectation}
                className={styles.overviewButton}>
                {t('dynamicbehavior.advanced.expectation.button')}
              </button>
              <button 
                onClick={(event) => onFocusIDClick(event, FocusPoints.DynamicBehaviorReality)}
                data-selected={activeFocusID === FocusPoints.DynamicBehaviorReality}
                className={styles.overviewButton}>
                {t('dynamicbehavior.advanced.reality.button')}
              </button>
            </div>
          }

          <div className={[whiteModeStyles.buttonContainer, styles.buttonContainer].join(' ')}>
            { activeDetailID !==  DetailSectionIDs.DecodingDynamicBehaviour && 
              <button className={whiteModeStyles.toConclusionButton} onClick={toNextDetail}>
                {t('thescience.buttons.next')}
              </button>
            }
            { activeDetailID ===  DetailSectionIDs.DecodingDynamicBehaviour && 
              <div className={whiteModeStyles.toConclusionButton} onClick={toConclusion}>
                {t('thescience.buttons.conclusion')}
              </div>
            }
          </div>
        </div>
      </div>

      { !isMobile &&
        <button
          className={[whiteModeStyles.toTimelineButton, styles.toTimelineButton].join(' ')}
          onClick={onToTimelineClick}>
          <ArrowLeftIcon />
          {t('thescience.buttons.timeline')}
        </button>
      }
    </div>
  );
}
